import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';

import useGroupLabels from 'hooks/useGroupLabels';
import { GROUPING_BASIS, formatGroupRange } from 'utils/groupLabel';
import styles from './ImpactReviewDialog.module.css';

// Program Edit Impact Review dialog (docs/program-edit-impact-design.html §4).
// Rendered from the 409 impact report returned by the program save/delete.
// One mental model: EVERYTHING listed applies together when the user presses
// Save Program; Cancel closes with nothing saved. The only interactive element
// is the acknowledgment checkbox on the signups section — an acknowledgment,
// not an action — which gates Save until ticked.

// A start list's group, which arrives in the stored form ("10", "13-17"): "U10" and "U13-U17" for an Under
// club, "10" and "13-17" for an Age club, "Y5" and "Y8-Y12" for a club that groups by year level.
const formatAgeGroup = (groupingBasis, ageGroup) => {
  if (!ageGroup) return '';
  if (
    groupingBasis === GROUPING_BASIS.YEAR_LEVEL ||
    groupingBasis === GROUPING_BASIS.AGE
  ) {
    return formatGroupRange(groupingBasis, ageGroup);
  }
  return /^\d/.test(ageGroup) ? `U${ageGroup.replace(/-/g, '-U')}` : ageGroup;
};

const plural = (count, singular, pluralWord) =>
  count === 1 ? singular : pluralWord || `${singular}s`;

const ImpactReviewDialog = ({
  isOpen,
  report,
  onCancel,
  onConfirm,
  saving,
  // Optional copy overrides — the defaults are the program-edit flow's; the
  // requirements-save flow reuses this dialog with its own wording (§16).
  title = 'Review Program Changes',
  description = "These program changes affect other parts of this meet's setup. Everything listed below will be applied together when you select Save Program. Nothing changes if you cancel.",
  confirmButtonText = 'Save Program',
}) => {
  const [cancellationsAcknowledged, setCancellationsAcknowledged] = useState(false);
  const { groupingBasis } = useGroupLabels();

  // A refreshed report (e.g. after a race-safety 409) resets the acknowledgment
  useEffect(() => {
    setCancellationsAcknowledged(false);
  }, [report]);

  if (!isOpen || !report) return null;

  const rolesRemoved = report.requirementsWithoutSignups || [];
  const openSignups = report.openSignups || [];
  const startLists = report.startLists || [];
  const eventRegistrations = report.eventRegistrations || [];

  const canSave = openSignups.length === 0 || cancellationsAcknowledged;

  const handleConfirm = () => {
    if (!canSave || saving) return;
    onConfirm({
      cancelSignupIds: openSignups.map((signup) => signup.volunteerSignupID),
      acknowledgeStartListIds: startLists.map((list) => list.eventStartListID),
      acknowledgeRegistrationEventCodes: eventRegistrations.map(
        (registration) => registration.eventType
      ),
    });
  };

  const withGroup = (name, groupName) =>
    groupName ? `${name} (${groupName})` : name;

  return (
    <CustomNewConfirm
      isOpen={isOpen}
      onClose={onCancel}
      onCancel={onCancel}
      title={title}
      description={description}
      confirmButtonText={saving ? 'Saving...' : confirmButtonText}
      confirmButtonVariant="danger"
      onConfirm={handleConfirm}
      isLoading={saving || !canSave}
    >
      <div className={styles.sections}>
        {rolesRemoved.length > 0 && (
          <div className={styles.section}>
            <div className={`${styles.sectionTitle} ${styles.info}`}>
              Volunteer roles to be removed — no signups
              <span className={styles.count}>
                {rolesRemoved.length} {plural(rolesRemoved.length, 'role')}
              </span>
            </div>
            <ul className={styles.itemList}>
              {rolesRemoved.map((requirement) => (
                <li key={requirement.meetVolunteerRequirementID}>
                  {withGroup(requirement.roleName, requirement.programGroupName)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {openSignups.length > 0 && (
          <div className={styles.section}>
            <div className={`${styles.sectionTitle} ${styles.action}`}>
              Volunteer signups to be cancelled
              <span className={styles.count}>
                {openSignups.length} {plural(openSignups.length, 'signup')}
              </span>
            </div>
            <ul className={styles.itemList}>
              {openSignups.map((signup) => (
                <li key={signup.volunteerSignupID}>
                  {signup.memberName} —{' '}
                  {withGroup(signup.roleName, signup.programGroupName)}
                </li>
              ))}
            </ul>
            <label className={styles.ackRow}>
              <input
                type="checkbox"
                className={styles.ackCheckbox}
                checked={cancellationsAcknowledged}
                onChange={(e) => setCancellationsAcknowledged(e.target.checked)}
              />
              <span>
                Cancel{' '}
                {openSignups.length === 1
                  ? 'this signup'
                  : `these ${openSignups.length} signups`}{' '}
                when I save
              </span>
            </label>
            {!cancellationsAcknowledged && (
              <div className={styles.ackHint}>
                {confirmButtonText} stays disabled until you confirm these
                cancellations.
              </div>
            )}
          </div>
        )}

        {startLists.length > 0 && (
          <div className={styles.section}>
            <div className={`${styles.sectionTitle} ${styles.destructive}`}>
              Start lists to be deleted
              <span className={styles.count}>
                {startLists.length} {plural(startLists.length, 'start list')}
              </span>
            </div>
            <ul className={styles.itemList}>
              {startLists.map((list) => (
                <li key={list.eventStartListID}>
                  {list.eventTypeName} — {formatAgeGroup(groupingBasis, list.ageGroup)}{' '}
                  {list.gender} — {list.roundCount}{' '}
                  {plural(list.roundCount, 'round')}, {list.athleteCount}{' '}
                  {plural(list.athleteCount, 'athlete')}
                </li>
              ))}
            </ul>
          </div>
        )}

        {eventRegistrations.length > 0 && (
          <div className={styles.section}>
            <div className={`${styles.sectionTitle} ${styles.destructive}`}>
              Athlete registrations to be updated
            </div>
            <ul className={styles.itemList}>
              {eventRegistrations.map((registration) => (
                <li key={registration.eventType}>
                  {registration.eventTypeName} — event removed from{' '}
                  {registration.athleteCount}{' '}
                  {plural(registration.athleteCount, "athlete's registration", "athletes' registrations")}
                  {registration.athletesLeftWithNoEvents > 0 &&
                    ` (${registration.athletesLeftWithNoEvents} ${plural(
                      registration.athletesLeftWithNoEvents,
                      'athlete'
                    )} will have no events left)`}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </CustomNewConfirm>
  );
};

ImpactReviewDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  report: PropTypes.shape({
    requirementsWithoutSignups: PropTypes.array,
    requirementsWithSignups: PropTypes.array,
    openSignups: PropTypes.array,
    startLists: PropTypes.array,
    eventRegistrations: PropTypes.array,
  }),
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  title: PropTypes.string,
  description: PropTypes.string,
  confirmButtonText: PropTypes.string,
};

export default ImpactReviewDialog;
